/**
 * TypeScript contract for the ownership-aware self-healing stop. Mirrors the
 * Rust DTOs (`src-tauri/src/recording_lifecycle/*`,
 * `src-tauri/src/online_backup/*`), which serialize `camelCase`. All consumption
 * is `isTauri()`-guarded and behind `useIsSelfHealingStopEnabled()`.
 */

/**
 * IPC error code Rust returns when a stop carries a `(recordingId, generation)`
 * that is not the current epoch. This is an expected rejection during a webview
 * reload / superseded tab / cross-epoch race — the FE bridge treats it as a
 * silent no-op, not a user-facing error.
 */
export const SELF_HEALING_STALE_EPOCH_CODE = 'SELF_HEALING_STALE_EPOCH';

/**
 * How a stop was initiated. Only `userStop` (the final stop) auto-triggers
 * note-gen. `pause` and `deviceChange` are user-driven but non-final segment
 * stops: they finalize + retain/upload (no data loss) and let resume / the new
 * device claim a fresh `recordingId`/epoch, but never generate. All others are
 * forced terminations that also never auto-trigger note-gen.
 */
export type StopReason =
  | 'userStop'
  | 'pause'
  | 'deviceChange'
  | 'feFallback'
  | 'startConvergence'
  | 'shutdown'
  | 'watchdogOrphanedNoLease'
  | 'watchdogNoProgress'
  | 'watchdogMaxDuration'
  | 'watchdogStopDeadline'
  | 'watchdogTerminalResponses'
  | 'watchdogChunkCeiling';

/** Client-side terminal classification. */
export type TerminalClass = 'finalized' | 'failed' | 'retained';

/** Where note-gen sources from + whether the backup is on the critical path. */
export type BackupMode = 'uploaded' | 'pendingAsync' | 'blocking' | 'retained';

/**
 * Superset of today's `StopUploadResult` (`{ totalChunks, totalBytes,
 * failedChunks, durationMs, backupUploaded, finishSucceeded }`). The extension
 * adds the terminal-outcome fields while preserving the existing ones so
 * `useMutateStopRecording.ts` keeps compiling.
 */
export interface TerminalOutcome {
  // existing StopUploadResult fields (unchanged)
  totalChunks: number;
  totalBytes: number;
  failedChunks: number;
  durationMs: number;
  /** Retained for back-compat; not used to gate the healthy note-gen path. */
  backupUploaded: boolean;
  finishSucceeded: boolean;
  // new
  recorderStopped: boolean;
  cadenceStopped: boolean;
  workerDrained: boolean;
  filesRetained: boolean;
  serverTerminalAck: 'reaped' | 'quarantined' | null;
  backup: BackupMode;
  classification: TerminalClass;
  reason: StopReason;
  recordingId: string;
  generation: number;
  /** Set when this stop handed unsent audio to the offline queue. */
  offlineHandoverLocalSessionId?: string;
}

/** Typed reconnect snapshot after a webview reload. */
export interface NativeStateSnapshot {
  state: 'active' | 'stopping' | 'terminal';
  recordingId: string;
  generation: number;
  terminalIntent: StopReason | null;
  /** Epoch millis. */
  lastProgressAt: number;
}

/** Identifies the epoch a lease heartbeat is for. */
export interface RecordingLeaseParams {
  recordingId: string;
  generation: number;
}

/**
 * Closed, PHI-free classification of the last backup-upload failure. Must not
 * carry a raw server body (a 404/reject body can contain session/patient
 * context) — the Rust side maps the response to one of these codes before it
 * reaches the IPC bus or analytics.
 */
export type BackupErrorCode =
  | 'SESSION_NOT_FOUND' // 404 on the session endpoint → terminal-evict
  | 'PERMANENT_REJECT' // non-retryable 4xx (excl. ignorable 422 too-small) → terminal-evict
  | 'AUTH_UNAVAILABLE' // no/expired token or logged-out → defer, retain, retry next launch/login
  | 'NETWORK' // transient connectivity failure → retry
  | 'SERVER_ERROR' // 5xx → retry
  | 'UNKNOWN'; // unclassified → retry under the attempt cap

/** FE-facing projection of a durable `backup_recovery` row. */
export interface BackupRecoveryRow {
  sessionId: string;
  recordingId: string;
  status: 'pending' | 'uploading' | 'uploaded' | 'abandonedRetained' | 'terminalEvicted';
  attemptCount: number;
  lastErrorCode: BackupErrorCode | null;
  createdAt: string;
}
